// eslint-disable-next-line spaced-comment
/*global HMR_ENABLED */

// Although we do move most polyfills to a separate file, we still need Promise and
// fetch in order to import code dynamically. So this is necessary.
import 'es6-promise/auto';
import "whatwg-fetch";

// All styling combined in styles.scss
// Critical styling is inlined from critical.scss in to a style tag in shell.html
import './styles/styles.scss';

import { hot } from "react-hot-loader/root";
import * as React from "react";
import { render as reactDOMRender, hydrate } from "react-dom";
import { Provider } from "react-redux";
import { ConnectedRouter } from "connected-react-router";
import { createBrowserHistory } from "history";
import { ScrollManager, WindowScroller as RSCWindowScroller } from "react-scroll-manager";
// @ts-ignore
import { detectBrowserUnsupportedFeatures } from 'polyfill-io-feature-detection';

import { connectWithPage } from "@montana/containers/page";
import { isBrowser, isFirefox, isIE11, isSafari } from "@montana/helpers/browser.helper";
import { requestIdleCallback } from "@montana/helpers/request-callback.helper";
import { configureStore, sagaMiddleware } from "./store";
import { App } from "./app.component";
import { appInit } from "./containers/app/app.actions";
import { rootSagas, bootstrapSagaB2C } from "./store/sagas";
import { setStaticSettings } from "./helpers/static-settings.helper";
import { getRefreshToken } from './helpers/auth-token.helper';
import { IDynamicSettings, IStaticSettings } from './models/app-settings';
import { refreshSecurityToken } from './services/authentication';
import { initDynamicSettings } from './containers/dynamic-settings/dynamic-settings.actions';
import { getLanguageFromUrl } from './helpers/market.helper';

const onClientReady = async () => {
    const path = `${window.location.pathname}${window.location.search}`;

    const {
        initialState: stringifiedInitialState,
        dynamicSettings: stringifiedDynamicSettings,
        staticSettings: stringifiedStaticSettings,
    } = window;

    // Deserializing from strings gives us a potentially huge performance boost in TTI.
    // Deserializing from strings gives us a potentially huge performance boost in TTI.
    // See the longer explanation in `entry-ssr.tsx`. :)
    const staticSettings: IStaticSettings = stringifiedStaticSettings ? JSON.parse(stringifiedStaticSettings) : {};
    const initialState = stringifiedInitialState ? JSON.parse(stringifiedInitialState) : undefined;

    if (isBrowser && initialState?.router.location) {
        initialState.router.location.hash = window.location.hash;
    }

    setStaticSettings(staticSettings);

    const history = createBrowserHistory();
    const store = configureStore(
        history,
        initialState
    );

    if (!initialState && stringifiedDynamicSettings) {
        const dynamicSettings: IDynamicSettings = stringifiedDynamicSettings ? JSON.parse(stringifiedDynamicSettings) : {};
        store.dispatch(initDynamicSettings(dynamicSettings, undefined, getLanguageFromUrl(path)));
    }

    sagaMiddleware.run(rootSagas, staticSettings.b2b);

    function init(): void {
        store.dispatch(appInit());
    }

    init();

    // TODO: Fix so this is not from a homecooked type
    if ((initialState && initialState.page.fetchError && initialState.page.fetchError.errorResponseCode === 401) || !initialState) {
        // So we were unauthorized... try to authorize.
        const refreshToken = getRefreshToken();
        if (refreshToken) {
            const authorized = await refreshSecurityToken(refreshToken);
            if (authorized) {
                sagaMiddleware.run(bootstrapSagaB2C, path);
            }
        }
    }

    const render = initialState !== undefined ? hydrate : reactDOMRender;

    document.documentElement.classList.add(isIE11 ? "ie11" : "notIE");
    document.documentElement.classList.add(isSafari ? "is-safari" : "not-safari");
    document.documentElement.classList.add(isFirefox ? "is-firefox" : "not-firefox");

    /**
     * WindowScroller from "react-scroll-manager" runs on component update.
     * This is a way to force the component to update when page data is refreshed.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const WindowScroller = connectWithPage(RSCWindowScroller as any);
    // @ts-ignore
    const HmrApp = HMR_ENABLED ? hot(App) : App; // <paris>That's hot!</paris>

    requestIdleCallback((): void => {
        render(
            <Provider store={store}>
                <ScrollManager history={history}>
                    <ConnectedRouter history={history}>
                        <WindowScroller>
                            <HmrApp />
                        </WindowScroller>
                    </ConnectedRouter>
                </ScrollManager>
            </Provider>,
            document.getElementById("root") as HTMLElement,
        );
    });
};


const neededFeatures = [
    "Array.prototype.find",
    "Array.prototype.fill",
    "Array.from",
    "Map",
    "Symbol",
];

// Use the detection engine from Polyfill.io, handily available as an NPM package,
// to find out if the browser lacks support of the features we need.
const unsupportedFeatures = detectBrowserUnsupportedFeatures(neededFeatures.join(","));

// If we're missing even a single feature, go ahead and get everything. This will
// introduce even more waiting for the user, but since it's a slow browser anyway we don't
// really care about dynamic speed at this point. If there are no missing features, however,
// we can go ahead and "boot" the site.
if (unsupportedFeatures.length) {
    // @ts-ignore
    import(/* webpackChunkName: "polyfills" */ "./client.polyfills.js").then(onClientReady);
} else {
    onClientReady();
}
